import React, { useState, useEffect, useRef, Suspense, lazy, useCallback } from 'react';
import { applyDomainFilterToQuery, getDomainFilterParams, useDomainQuery } from '../../../hooks/useDomainQuery';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { supabase } from '../../../lib/supabase';
import { useTenantContext } from '../../../context/TenantContext';
import { MediaSelector } from '../../../components/media/MediaSelector';
import { useToast } from '../../../components/ui/use-toast';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import * as z from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { Loader2, Save, ArrowLeft, Image, X, Eye, EyeOff, Maximize2, Minimize2, Type, Moon, Sun, ZoomIn, ZoomOut, FolderOpen } from 'lucide-react';
import CustomQuillEditor from '../../../components/editor/CustomQuillEditor';
import { BlogCategorySelect } from '../../../components/admin/blog/BlogCategorySelect';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const blogPostSchema = z.object({
  title: z.string().min(1, 'Le titre est requis'),
  slug: z.string().min(1, 'Le slug est requis'),
  excerpt: z.string().optional(),
  content: z.string().min(1, 'Le contenu est requis'),
  status: z.enum(['draft', 'published']),
  featured_image: z
    .instanceof(FileList)
    .optional()
    .refine((files) => {
      if (files && files.length > 0) {
        return files[0].size <= MAX_FILE_SIZE;
      }
      return true;
    }, 'La taille maximale du fichier est de 5MB')
    .refine((files) => {
      if (files && files.length > 0) {
        return ACCEPTED_IMAGE_TYPES.includes(files[0].type);
      }
      return true;
    }, 'Format accepté : .jpg, .jpeg, .png, .webp'),
  gallery_images: z
    .instanceof(FileList)
    .optional()
    .refine((files) => {
      if (files && files.length > 0) {
        return Array.from(files).every(file => file.size <= MAX_FILE_SIZE);
      }
      return true;
    }, 'La taille maximale par fichier est de 5MB')
    .refine((files) => {
      if (files && files.length > 0) {
        return Array.from(files).every(file => ACCEPTED_IMAGE_TYPES.includes(file.type));
      }
      return true;
    }, 'Format accepté : .jpg, .jpeg, .png, .webp'),
  categories: z.array(z.string()).optional(),
  tags: z.string().optional(),
});

type BlogPostFormValues = z.infer<typeof blogPostSchema>;

interface Category {
  id: string;
  name: string;
}

export const BlogPostEdit = () => {
  useCheckPermissions(['admin']);
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  
  // Utiliser useDomainQuery au niveau du composant (pas dans une fonction)
  const { getFilterParams } = useDomainQuery();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [galleryPreviews, setGalleryPreviews] = useState<string[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const [showGalleryMediaSelector, setShowGalleryMediaSelector] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [isDragging, setIsDragging] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const fullscreenToolbarRef = useRef<HTMLDivElement>(null);
  const [isToolbarHidden, setIsToolbarHidden] = useState(false);
  const hideToolbarTimeout = useRef<NodeJS.Timeout>();
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const quillRef = useRef<any>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const form = useForm<BlogPostFormValues>({
    resolver: zodResolver(blogPostSchema),
    defaultValues: {
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      status: 'draft' as const,
      categories: [],
      tags: '',
    },
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Récupérer directement les catégories du domaine actuel seulement
        let categoriesQuery = supabase
          .from('blog_categories')
          .select('id, name')
          .order('name');

        // Appliquer le filtre domaine avec fallback sur tenant_id
        categoriesQuery = applyDomainFilterToQuery(categoriesQuery);

        const { data, error } = await categoriesQuery;

        if (error) throw error;
        setCategories(data || []);
      } catch (error) {
        console.error('Erreur lors de la récupération des catégories:', error);
        toast({
          variant: 'destructive',
          title: 'Erreur',
          description: 'Impossible de charger les catégories',
        });
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchPost = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        // Récupérer l'article
        const { data: post, error } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        if (!post) {
          toast({
            variant: 'destructive',
            title: 'Erreur',
            description: 'Article non trouvé',
          });
          navigate('/admin/blog');
          return;
        }

        // Récupérer les catégories de l'article
        const { data: postCategories, error: categoriesError } = await supabase
          .from('blog_post_categories')
          .select('category_id')
          .eq('post_id', id);

        if (categoriesError) throw categoriesError;

        const categoryIds = postCategories?.map(pc => pc.category_id) || [];
        setSelectedCategories(categoryIds);

        // Traiter les tags
        let formattedTags = '';
        if (post.tags && Array.isArray(post.tags)) {
          formattedTags = post.tags.join(', ');
        } else if (post.tags && typeof post.tags === 'string') {
          formattedTags = post.tags;
        }

        // Remplir le formulaire
        form.reset({
          title: post.title || '',
          slug: post.slug || '',
          excerpt: post.excerpt || '',
          content: post.content || '',
          status: (post.status as 'draft' | 'published') || 'draft',
          categories: categoryIds,
          tags: formattedTags,
        });

        if (post.featured_image_url) {
          setImagePreview(post.featured_image_url);
        }
        
        if (post.gallery_images && post.gallery_images.length > 0) {
          setGalleryPreviews(post.gallery_images);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'article:', error);
        toast({
          variant: 'destructive',
          title: 'Erreur',
          description: 'Impossible de charger l\'article',
        });
        navigate('/admin/blog');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id, navigate, form]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    let file: File | null = null;
    
    if ('dataTransfer' in e) {
      // Cas du glisser-déposer
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        file = e.dataTransfer.files[0];
      }
    } else if (e.target.files && e.target.files.length > 0) {
      // Cas de la sélection de fichier classique
      file = e.target.files[0];
    }
    
    if (file) {
      // Vérifier la taille du fichier
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: 'Erreur',
          description: 'La taille du fichier ne doit pas dépasser 5MB',
          variant: 'destructive',
        });
        return;
      }
      
      // Vérifier le type du fichier
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast({
          title: 'Erreur',
          description: 'Format de fichier non accepté. Utilisez JPG, PNG ou WEBP',
          variant: 'destructive',
        });
        return;
      }
      
      // Créer un FileList pour le formulaire
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      form.setValue('featured_image', dataTransfer.files as any, { shouldValidate: true });
      
      // Afficher l'aperçu de l'image
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setImagePreview(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const removeImage = () => {
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    form.setValue('featured_image', undefined);
  };

  const handleGalleryImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newPreviews: string[] = [];
      Array.from(files).forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push(reader.result as string);
          if (newPreviews.length === files.length) {
            setGalleryPreviews(prev => [...prev, ...newPreviews]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeGalleryImage = (index: number) => {
    setGalleryPreviews(prev => prev.filter((_, i) => i !== index));
    if (galleryInputRef.current) {
      galleryInputRef.current.value = '';
    }
  };

  const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
    const res = await fetch(dataUrl);
    return await res.blob();
  };

  const uploadContentImages = async (html: string, postId: string | undefined): Promise<string> => {
    if (!html) return html;
    const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gim;
    let match: RegexpExecArray | null;
    let index = 0;
    let updatedHtml = html;
    const { tenantId } = getFilterParams(); // Récupérer le tenant ID pour l'upload
    while ((match = imgRegex.exec(html)) !== null) {
      const src = match[1];
      if (src.startsWith('data:')) {
        try {
          const blob = await dataUrlToBlob(src);
          const ext = blob.type.split('/')[1] || 'png';
          const fileName = `${Date.now()}_${index}.${ext}`;
          const filePath = `tenant/${tenantId}/blog/${fileName}`; // Utiliser le tenant ID

          const { error: uploadError } = await supabase.storage
            .from('candidates')
            .upload(filePath, blob, { 
              upsert: true,
              cacheControl: '3600',
              contentType: blob.type,
            });
          if (uploadError) {
            console.error('Erreur upload image content:', uploadError);
            continue;
          }
          const { data: urlData } = supabase.storage.from('candidates').getPublicUrl(filePath);
          if (urlData?.publicUrl) {
            const regexSrc = new RegExp(src.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'g');
updatedHtml = updatedHtml.replace(regexSrc, urlData.publicUrl);
          }
        } catch (err) {
          console.error('Erreur conversion/upload image content:', err);
        }
      }
      index += 1;
    }
    return updatedHtml;
  };

  const onSubmit = async (values: BlogPostFormValues) => {
    setSaving(true);
    try {
      let featured_image_url = imagePreview;
      let postId = id;
      // Récupérer tenantId et domain depuis le hook déjà initialisé
      const { tenantId, domain } = getFilterParams();

      // Traiter les images encodées en base64 dans le contenu et les remplacer par les URLs après upload
      const processedContent = await uploadContentImages(values.content, postId);
      
      // Si c'est un nouvel article, on le crée d'abord
      if (!id) {
        
        const { data: newPost, error: createError } = await supabase
          .from('blog_posts')
          .insert({
            title: values.title,
            slug: values.slug,
            excerpt: values.excerpt,
            content: processedContent, // Utiliser le contenu traité avec les images uploadées
            status: values.status,
            tenant_id: tenantId,
            domain: domain, // Ajouter le domaine actuel
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            published_at: values.status === 'published' ? new Date().toISOString() : null,
          })
          .select()
          .single();
          
        if (createError) throw createError;
        postId = newPost.id;
      }
      
      // Upload de l'image si présente
      if (values.featured_image && values.featured_image.length > 0) {
        try {
          const file = values.featured_image[0];
          const fileExt = file.name.split('.').pop();
          const fileName = `blog_${postId}_${Date.now()}.${fileExt}`;
          const filePath = `tenant/${tenantId}/${fileName}`;
          
          const { error: uploadError } = await supabase.storage
            .from('candidates')
            .upload(filePath, file, { 
              upsert: true,
              cacheControl: '3600'
            });
            
          if (uploadError) throw uploadError;
          
          const { data: urlData } = supabase.storage
            .from('candidates')
            .getPublicUrl(filePath);
            
          featured_image_url = urlData.publicUrl;
        } catch (error) {
          console.error('Erreur lors du téléchargement de l\'image:', error);
          toast({
            variant: 'destructive',
            title: 'Erreur',
            description: 'Impossible de télécharger l\'image',
          });
        }
      }
      
      // Upload des images de galerie si présentes
      let gallery_images_urls: string[] = [];
      if (values.gallery_images && values.gallery_images.length > 0) {
        try {
          const uploadPromises = Array.from(values.gallery_images).map(async (file, index) => {
            const fileExt = file.name.split('.').pop();
            const fileName = `${postId}_gallery_${index}_${Date.now()}.${fileExt}`;
            const filePath = `tenant/${tenantId}/gallery/${fileName}`;
            
            const { error: uploadError } = await supabase.storage
              .from('candidates')
              .upload(filePath, file, { 
                upsert: true,
                cacheControl: '3600'
              });
              
            if (uploadError) throw uploadError;
            
            const { data: urlData } = supabase.storage
              .from('candidates')
              .getPublicUrl(filePath);
              
            return urlData.publicUrl;
          });
          
          gallery_images_urls = await Promise.all(uploadPromises);
        } catch (error) {
          console.error('Erreur lors du téléchargement des images de galerie:', error);
          toast({
            variant: 'destructive',
            title: 'Erreur',
            description: 'Impossible de télécharger les images de galerie',
          });
        }
      }
      
      // Conserver les anciennes images de galerie et ajouter les nouvelles
      const existingGalleryImages = galleryPreviews.filter(url => url.startsWith('http'));
      const allGalleryImages = [...existingGalleryImages, ...gallery_images_urls];
      
      // Convertir les tags
      let tags: string[] = [];
      if (values.tags) {
        tags = values.tags.split(',').map(tag => tag.trim()).filter(tag => tag !== '');
      }
      
      // Traiter les images base64 du contenu puis mise à jour de l'article
      if (id) {
        // Si c'est un article existant
        const { error } = await supabase
          .from('blog_posts')
          .update({
            title: values.title,
            slug: values.slug,
            excerpt: values.excerpt,
            content: processedContent, // Utiliser le contenu traité avec les images uploadées
            featured_image_url,
            gallery_images: allGalleryImages,
            status: values.status,
            updated_at: new Date().toISOString(),
            published_at: values.status === 'published' 
              ? new Date().toISOString() 
              : null,
            tags,
            domain: domain, // Ajouter le domaine actuel lors de la mise à jour
          })
          .eq('id', id);
        
        if (error) throw error;
      } else if (featured_image_url) {
        // Si c'est un nouvel article et qu'on a uploadé une image
        const { error: updateImageError } = await supabase
          .from('blog_posts')
          .update({
            featured_image_url,
            gallery_images: allGalleryImages,
            tags,
          })
          .eq('id', postId);
          
        if (updateImageError) throw updateImageError;
      }
      
      // Supprimer les anciennes relations de catégories si c'est un article existant
      if (id) {
        const { error: deleteError } = await supabase
          .from('blog_post_categories')
          .delete()
          .eq('post_id', id);
          
        if (deleteError) throw deleteError;
      }
      
      // Ajouter les nouvelles relations de catégories
      if (selectedCategories && selectedCategories.length > 0) {
        const categoryRelations = selectedCategories.map(categoryId => ({
          post_id: postId,
          category_id: categoryId,
        }));
        
        const { error: categoriesError } = await supabase
          .from('blog_post_categories')
          .insert(categoryRelations);
          
        if (categoriesError) throw categoriesError;
      }
      
      toast({
        title: 'Succès',
        description: id ? 'L\'article a été enregistré' : 'L\'article a été créé',
      });
      
      // Rediriger vers la liste des articles
      navigate('/admin/blog');
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de l\'article:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible d\'enregistrer l\'article',
      });
    } finally {
      setSaving(false);
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
      .replace(/[^a-z0-9]+/g, '-') // Remplace les caractères spéciaux par des tirets
      .replace(/^-+|-+$/g, '') // Supprime les tirets en début et fin
      .substring(0, 60); // Limite la longueur du slug
  };

  const generateExcerpt = (content: string, maxLength = 160) => {
    // Supprime les balises HTML et les espaces superflus
    const text = content
      .replace(/<[^>]*>?/gm, '')
      .replace(/\s+/g, ' ')
      .trim();
    
    // Retourne les premiers caractères avec une coupure propre
    return text.length > maxLength 
      ? text.substring(0, text.lastIndexOf(' ', maxLength)) + '...'
      : text;
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    // Met à jour le titre
    form.setValue('title', title);
    
    // Génère et met à jour le slug uniquement si c'est une nouvelle entrée ou si le slug n'a pas été modifié manuellement
    if (!id || form.getValues('slug') === '') {
      form.setValue('slug', generateSlug(title), { shouldValidate: true });
    }
  };

  const handleContentChange = (content: string) => {
    // La fonction existe déjà, on la garde
    form.setValue('content', content);
    
    // Génère un extrait si le champ est vide
    if (!form.getValues('excerpt') && content) {
      const excerpt = generateExcerpt(content);
      form.setValue('excerpt', excerpt);
    }
  };

  const handleSelectionChange = (range: any, source: string, editor: any) => {
    if (range) {
      const bounds = editor.getBounds(range.index, range.length);
      setMenuPosition({
        x: bounds.left,
        y: bounds.top - 40 // Positionne le menu au-dessus de la sélection
      });
      setShowContextMenu(true);
    } else {
      setShowContextMenu(false);
    }
  };

  /* Drag & Drop dans l'éditeur */
  const handleEditorDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleEditorDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleEditorDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (!files.length || !quillRef.current) return;

    const quill = quillRef.current.getEditor();
    const range = quill.getSelection(true);
    const position = range ? range.index : 0;

    for (const file of files) {
      // Only images for now
      if (!file.type.startsWith('image/')) continue;
      
      try {
        // Upload de l'image dans Supabase Storage puis insertion de l'URL
        const { tenantId } = getFilterParams(); // Récupérer le tenant ID
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `tenant/${tenantId}/blog/${fileName}`; // Utiliser le tenant ID

        // Upload vers le bucket "candidates"
        const { error: uploadError } = await supabase.storage
          .from('candidates')
          .upload(filePath, file, { 
            upsert: true,
            cacheControl: '3600'
          });

        if (uploadError) {
          console.error('Erreur upload Supabase:', uploadError);
          toast({
            title: 'Erreur',
            description: 'Impossible de téléverser l\'image',
            variant: 'destructive',
          });
          continue;
        }

        const { data: urlData } = supabase.storage.from('candidates').getPublicUrl(filePath);
        const publicUrl = urlData.publicUrl;

        if (publicUrl && quillRef.current) {
          quill.insertEmbed(position, 'image', publicUrl, 'user');
          quill.setSelection(position + 1);
          // Mettre à jour le contenu HTML du formulaire
          const content = quillRef.current.getEditor().root.innerHTML;
          form.setValue('content', content);
        }
        
      } catch (error) {
        console.error('Erreur lors du traitement de l\'image:', error);
        toast({
          title: 'Erreur',
          description: 'Une erreur est survenue lors du traitement de l\'image',
          variant: 'destructive',
        });
      }
    }
  };

  // Gestion du mode plein écran
  const toggleFullscreen = () => {
    if (!editorRef.current) return;
    
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(console.error);
      setIsFullscreen(false);
    } else {
      editorRef.current.requestFullscreen({
        navigationUI: 'hide',
      }).then(() => {
        setIsFullscreen(true);
        // Masquer automatiquement la barre d'outils après 3 secondes d'inactivité
        setupToolbarAutoHide();
      }).catch(console.error);
    }
  };

  // Gestion de la taille de police
  const adjustFontSize = (delta: number) => {
    setFontSize(prev => Math.max(12, Math.min(24, prev + delta)));
  };

  // Configuration du masquage automatique de la barre d'outils
  const setupToolbarAutoHide = () => {
    if (!editorRef.current) return;
    
    const handleMouseMove = () => {
      setIsToolbarHidden(false);
      
      // Réinitialiser le timeout
      if (hideToolbarTimeout.current) {
        clearTimeout(hideToolbarTimeout.current);
      }
      
      // Cacher la barre d'outils après 3 secondes d'inactivité
      hideToolbarTimeout.current = setTimeout(() => {
        setIsToolbarHidden(true);
      }, 3000);
    };
    
    // Nettoyer l'ancien écouteur
    editorRef.current.removeEventListener('mousemove', handleMouseMove);
    // Ajouter le nouvel écouteur
    editorRef.current.addEventListener('mousemove', handleMouseMove);
    
    // Démarrer avec la barre d'outils visible
    handleMouseMove();
    
    // Nettoyage
    return () => {
      if (editorRef.current) {
        editorRef.current.removeEventListener('mousemove', handleMouseMove);
      }
      if (hideToolbarTimeout.current) {
        clearTimeout(hideToolbarTimeout.current);
      }
    };
  };

  // Gestion du mode sombre
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Enter pour sauvegarder
      if (e.ctrlKey && e.key === 'Enter') {
        form.handleSubmit(onSubmit)();
      }
      // Ctrl+B pour gras
      else if (e.ctrlKey && e.key === 'b') {
        e.preventDefault();
        handleFormat('bold');
      }
      // Ctrl+I pour italique
      else if (e.ctrlKey && e.key === 'i') {
        e.preventDefault();
        handleFormat('italic');
      }
      // Ctrl+K pour ajouter un lien
      else if (e.ctrlKey && e.key === 'k') {
        e.preventDefault();
        handleFormat('link');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [form]);

  const handleFormat = (format: string, value?: any) => {
    if (!quillRef.current) return;
    
    const quill = quillRef.current.getEditor();
    const range = quill.getSelection();
    
    if (range) {
      if (format === 'link') {
        const url = window.prompt('Entrez l\'URL:');
        if (url) {
          quill.format('link', url);
        }
      } else {
        quill.format(format, value);
      }
    }
    setShowContextMenu(false);
  };

  const handleCategoryChange = (selectedIds: string[]) => {
    setSelectedCategories(selectedIds);
    form.setValue('categories', selectedIds, { shouldValidate: true });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="container mx-auto py-6 flex justify-center items-center min-h-[500px]">
          <Loader2 className="animate-spin mr-2" size={24} />
          <span>Chargement de l'article...</span>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto py-6 px-6">
        <div className="flex justify-between items-center mb-6">
          <Button variant="outline" onClick={() => navigate('/admin/blog')} className="bg-white/5 border-white/10 text-white hover:bg-white/10">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à la liste
          </Button>
          <div className="flex space-x-2">
            <Button
              variant={form.watch('status') === 'draft' ? 'outline' : 'default'}
              onClick={() => form.setValue('status', 'published')}
              disabled={saving}
              className={form.watch('status') === 'draft' ? 'bg-white/5 border-white/10 text-white hover:bg-white/10' : 'bg-yellow-500 hover:bg-yellow-600 text-black'}
            >
              <Eye className="mr-2 h-4 w-4" />
              Publier
            </Button>
            <Button
              variant={form.watch('status') === 'published' ? 'outline' : 'default'}
              onClick={() => form.setValue('status', 'draft')}
              disabled={saving}
              className={form.watch('status') === 'published' ? 'bg-white/5 border-white/10 text-white hover:bg-white/10' : 'bg-yellow-500 hover:bg-yellow-600 text-black'}
            >
              <EyeOff className="mr-2 h-4 w-4" />
              Brouillon
            </Button>
            {form.watch('status') === 'published' && id && (
              <Button
                type="button"
                variant="outline"
                onClick={() => window.open(`/blog/${form.getValues('slug')}`, '_blank')}
                className="bg-blue-600 hover:bg-blue-700 text-white border-transparent"
              >
                <Eye className="mr-2 h-4 w-4" />
                Voir l'article
              </Button>
            )}
            <Button 
              type="submit" 
              form="blog-post-form" 
              disabled={saving}
              onClick={() => form.handleSubmit(onSubmit)()}
              className="bg-yellow-500 hover:bg-yellow-600 text-black ml-2"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Enregistrer
                </>
              )}
            </Button>
          </div>
        </div>

        <Form {...form} id="blog-post-form" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                <CardContent className="pt-6">
                  <div className="space-y-6">
                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-200 font-medium">Titre</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Titre de l'article" 
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                handleTitleChange(e);
                              }}
                              className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                            />
                          </FormControl>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="slug"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-200 font-medium">Slug</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="slug-de-l-article" 
                              {...field} 
                              className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                            />
                          </FormControl>
                          <FormDescription className="text-gray-400 text-sm">
                            Identifiant unique pour l'URL de l'article (ex: /blog/mon-article)
                          </FormDescription>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="excerpt"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-200 font-medium">Extrait</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Bref résumé de l'article (laissé vide pour générer automatiquement à partir du contenu)" 
                              {...field} 
                              rows={3}
                              className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                            />
                          </FormControl>
                          <FormDescription className="text-gray-400 text-sm">
                            Cet extrait sera affiché dans les listes d'articles et les aperçus.
                          </FormDescription>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="content"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex justify-between items-center">
                            <FormLabel className="text-gray-200 font-medium">Contenu</FormLabel>
                            <div className="flex space-x-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-sm text-blue-400 hover:text-blue-300"
                                onClick={() => setShowPreview(!showPreview)}
                              >
                                {showPreview ? 'Masquer l\'aperçu' : 'Aperçu'}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-sm text-blue-400 hover:text-blue-300"
                                onClick={() => setIsHtmlMode(!isHtmlMode)}
                              >
                                {isHtmlMode ? 'Mode Visuel' : 'Mode HTML'}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-sm text-blue-400 hover:text-blue-300 transition-all duration-200"
                                onClick={toggleFullscreen}
                                title="Plein écran (F11)"
                              >
                                {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                              </Button>
                            </div>
                          </div>
                          <FormControl>
                            <div 
                              ref={editorRef}
                              className={`relative transition-all duration-200 ${isFullscreen ? 'fixed inset-0 z-50 p-0 overflow-y-scroll' : 'min-h-[300px]'} ${isDarkMode ? 'dark bg-gray-900' : 'bg-white'} ${isDragging ? 'ring-2 ring-blue-500 bg-blue-500/10' : ''}`}
                              onDragOver={handleEditorDragOver}
                              onDragLeave={handleEditorDragLeave}
                              onDrop={handleEditorDrop}
                              onFullScreenChange={() => {
                                const isFullscreen = !!document.fullscreenElement;
                                setIsFullscreen(isFullscreen);
                                if (!isFullscreen) {
                                  setIsToolbarHidden(false);
                                  if (hideToolbarTimeout.current) {
                                    clearTimeout(hideToolbarTimeout.current);
                                  }
                                }
                              }}
                              style={isFullscreen ? {
                                fontSize: `${fontSize}px`,
                                lineHeight: '1.6',
                                height: 'calc(100vh - 40px)' // Hauteur ajustée
                              } : {}}
                            >
                              {isFullscreen && (
                                <div 
                                  ref={fullscreenToolbarRef}
                                  className={`fixed top-0 left-0 right-0 bg-gray-800 text-white p-2 flex items-center justify-between transition-transform duration-300 z-50 ${isToolbarHidden ? '-translate-y-full' : 'translate-y-0'}`}
                                >
                                  <div className="flex space-x-2">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-white hover:bg-gray-700"
                                      onClick={() => document.exitFullscreen()}
                                      title="Quitter le mode plein écran (Échap)"
                                    >
                                      <Minimize2 size={16} />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-white hover:bg-gray-700"
                                      onClick={toggleDarkMode}
                                      title={`Basculer en mode ${isDarkMode ? 'clair' : 'sombre'}`}
                                    >
                                      {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
                                    </Button>
                                    <div className="flex items-center space-x-1 bg-gray-700 rounded-md px-2">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="text-white hover:bg-gray-600 h-6 w-6 p-0"
                                        onClick={() => adjustFontSize(-1)}
                                        disabled={fontSize <= 12}
                                        title="Réduire la taille de police"
                                      >
                                        <ZoomOut size={14} />
                                      </Button>
                                      <span className="text-xs w-8 text-center">{fontSize}px</span>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="text-white hover:bg-gray-600 h-6 w-6 p-0"
                                        onClick={() => adjustFontSize(1)}
                                        disabled={fontSize >= 24}
                                        title="Augmenter la taille de police"
                                      >
                                        <ZoomIn size={14} />
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="flex space-x-2">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-white hover:bg-gray-700"
                                      onClick={() => setIsHtmlMode(!isHtmlMode)}
                                      title={isHtmlMode ? 'Mode Visuel' : 'Mode HTML'}
                                    >
                                      <Type size={16} />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-white hover:bg-gray-700"
                                      onClick={() => setShowPreview(!showPreview)}
                                      title={showPreview ? 'Masquer l\'aperçu' : 'Afficher l\'aperçu'}
                                    >
                                      {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </Button>
                                  </div>
                                </div>
                              )}
                              {showPreview && (
                                <div className={`absolute inset-0 z-10 ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} p-4 overflow-auto rounded-md border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} ${isFullscreen ? 'top-0 left-0 right-0 bottom-0' : ''}`}>
                                  <div dangerouslySetInnerHTML={{ __html: form.getValues('content') || '' }} />
                                </div>
                              )}
                              {isHtmlMode ? (
                                <textarea
                                  value={field.value || ''}
                                  onChange={(e) => {
                                    field.onChange(e.target.value);
                                    handleContentChange(e.target.value);
                                  }}
                                  className="w-full min-h-[300px] p-2 bg-gray-800 text-white font-mono text-sm rounded-md border border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  spellCheck="false"
                                />
                              ) : (
                              <div className="relative">
                                {showContextMenu && (
                                  <div 
                                    className="absolute z-50 bg-gray-800 border border-gray-700 rounded-md shadow-lg p-1 flex space-x-1"
                                    style={{
                                      left: `${menuPosition.x}px`,
                                      top: `${menuPosition.y}px`,
                                      transform: 'translateX(-50%)'
                                    }}
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleFormat('bold');
                                      }}
                                      className="p-1 rounded hover:bg-gray-700 text-white"
                                      title="Gras"
                                    >
                                      <strong>B</strong>
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleFormat('italic');
                                      }}
                                      className="p-1 rounded hover:bg-gray-700 text-white italic"
                                      title="Italique"
                                    >
                                      I
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleFormat('link');
                                      }}
                                      className="p-1 rounded hover:bg-gray-700 text-white"
                                      title="Lien"
                                    >
                                      🔗
                                    </button>
                                  </div>
                                )}
                                <div className="mt-2">
                                  <CustomQuillEditor
                                    value={field.value || ''}
                                    onChange={(content) => {
                                      field.onChange(content);
                                      handleContentChange(content);
                                    }}
                                    className="[&_.ql-toolbar]:bg-gray-900 [&_.ql-container]:bg-black [&_.ql-editor]:min-h-[300px] [&_.ql-editor]:text-white [&_.ql-toolbar_button]:text-white [&_.ql-toolbar_button]:hover:bg-gray-800 [&_.ql-toolbar_button.ql-active]:bg-yellow-500 [&_.ql-toolbar_button.ql-active]:text-black [&_.ql-border-bottom]:border-gray-800 [&_.ql-stroke]:stroke-white [&_.ql-fill]:fill-white border border-white/10 rounded-md"
                                  />
                                </div>
                              </div>
                              )}
                            </div>
                          </FormControl>
                          <FormDescription className="text-gray-400 text-sm">
                            . Utilisez l'éditeur pour mettre en forme votre contenu.
                          </FormDescription>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-medium mb-4 text-gray-200">Image à la une</h3>
                  <FormField
                    control={form.control}
                    name="featured_image"
                    render={({ field: { value, onChange, ...fieldProps } }) => (
                      <FormItem>
                        <FormControl>
                          <div className="space-y-4">
                            <div className="space-y-4">
                              <div className="flex items-center gap-4">
                                <Input
                                  type="file"
                                  id="featured-image-upload"
                                  className="hidden"
                                  accept=".jpg,.jpeg,.png,.webp"
                                  onChange={(e) => {
                                    onChange(e.target.files);
                                    handleImageChange(e);
                                  }}
                                  {...fieldProps}
                                />
                                <div className="flex gap-2">
                                  <label
                                    htmlFor="featured-image-upload"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-white bg-white/5 border border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
                                  >
                                    <Image className="h-5 w-5" />
                                    Uploader
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => setShowMediaSelector(true)}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-white bg-gray-700 hover:bg-gray-600 transition-colors cursor-pointer"
                                  >
                                    <FolderOpen className="h-5 w-5" />
                                    Médiathèque
                                  </button>
                                </div>
                              </div>
                              
                              {imagePreview ? (
                                <div 
                                  className="relative aspect-video rounded-xl overflow-hidden group border border-white/10"
                                  onDrop={handleImageChange}
                                  onDragOver={handleDragOver}
                                >
                                  <img
                                    src={imagePreview}
                                    alt="Aperçu"
                                    className="object-cover w-full h-full"
                                  />
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="icon"
                                    className="absolute top-2 right-2 p-2 bg-red-500/80 hover:bg-red-500 rounded-xl text-white transition-colors opacity-0 group-hover:opacity-100"
                                    onClick={removeImage}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <div 
                                  className="aspect-video rounded-xl border-2 border-dashed border-white/20 bg-white/5 flex flex-col items-center justify-center gap-2 p-6 text-center cursor-pointer hover:border-yellow-500/50 transition-colors"
                                  onClick={() => document.getElementById('featured-image-upload')?.click()}
                                  onDrop={handleImageChange}
                                  onDragOver={handleDragOver}
                                >
                                  <Image className="h-10 w-10 text-yellow-500" />
                                  <p className="text-white font-medium">Glissez-déposez une image ici</p>
                                  <p className="text-sm text-gray-400">ou cliquez pour sélectionner</p>
                                  <p className="text-xs text-gray-500 mt-1">JPG, PNG, WEBP - 5MB max</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </FormControl>
                        <FormDescription className="text-gray-400 text-sm">
                          Image principale de l'article. Formats acceptés : JPG, PNG, WebP. Taille max : 5MB.
                        </FormDescription>
                        <FormMessage className="text-red-400" />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-medium mb-4 text-gray-200">Galerie d'images</h3>
                  <FormField
                    control={form.control}
                    name="gallery_images"
                    render={({ field: { value, onChange, ref, ...fieldProps } }) => (
                      <FormItem>
                        <FormControl>
                          <div className="space-y-4">
                            <div className="flex items-center gap-4">
                              <Input
                                type="file"
                                id="gallery-images-upload"
                                className="hidden"
                                accept=".jpg,.jpeg,.png,.webp"
                                multiple
                                {...fieldProps}
                                ref={galleryInputRef}
                                onChange={(e) => {
                                  onChange(e.target.files);
                                  handleGalleryImagesChange(e);
                                }}
                              />
                              <label
                                htmlFor="gallery-images-upload"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-white bg-white/5 border border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
                              >
                                <Image className="h-5 w-5" />
                                Uploader
                              </label>
                              <button
                                type="button"
                                onClick={() => setShowGalleryMediaSelector(true)}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-white bg-gray-700 hover:bg-gray-600 transition-colors cursor-pointer"
                              >
                                <FolderOpen className="h-5 w-5" />
                                Médiathèque
                              </button>
                            </div>
                            {galleryPreviews.length > 0 && (
                              <div className="grid grid-cols-2 gap-4">
                                {galleryPreviews.map((preview, index) => (
                                  <div key={index} className="relative aspect-video rounded-xl overflow-hidden group border border-white/10">
                                    <img
                                      src={preview}
                                      alt={`Galerie ${index + 1}`}
                                      className="object-cover w-full h-full"
                                    />
                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <Button
                                      type="button"
                                      variant="destructive"
                                      size="sm"
                                      className="absolute top-2 right-2 p-2 bg-red-500/80 hover:bg-red-500 rounded-xl text-white transition-colors opacity-0 group-hover:opacity-100 w-8 h-8"
                                      onClick={() => removeGalleryImage(index)}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </FormControl>
                        <FormDescription className="text-gray-400 text-sm">
                          Images supplémentaires pour la galerie de l'article. Formats acceptés : JPG, PNG, WebP. Taille max : 5MB par image.
                        </FormDescription>
                        <FormMessage className="text-red-400" />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-medium mb-4 text-gray-200">Statut de publication</h3>
                  <div className="flex items-center space-x-2 mb-4">
                    <Badge variant={form.watch('status') === 'published' ? 'default' : 'secondary'} className={form.watch('status') === 'published' ? 'bg-yellow-500 text-black' : 'bg-gray-700 text-gray-300'}>
                      {form.watch('status') === 'published' ? 'Publié' : 'Brouillon'}
                    </Badge>
                  </div>
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-white/10 p-4 bg-white/5">
                        <FormControl>
                          <Checkbox
                            checked={field.value === 'published'}
                            onCheckedChange={(checked) => {
                              field.onChange(checked ? 'published' : 'draft');
                            }}
                            className="border-white/30 data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-gray-200 font-medium">
                            Publier l'article
                          </FormLabel>
                          <FormDescription className="text-gray-400 text-sm">
                            L'article sera visible par tous les visiteurs
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-medium mb-4 text-gray-200">Catégories</h3>
                  <div className="bg-white/10 p-4 rounded-md border border-white/10">
                    <BlogCategorySelect
                      categories={categories}
                      selectedCategories={selectedCategories}
                      onChange={handleCategoryChange}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-medium mb-4 text-gray-200">Tags</h3>
                  <FormField
                    control={form.control}
                    name="tags"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input 
                            placeholder="tag1, tag2, tag3" 
                            {...field} 
                            className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" 
                          />
                        </FormControl>
                        <FormDescription className="text-gray-400 text-sm">
                          Séparez les tags par des virgules
                        </FormDescription>
                        <FormMessage className="text-red-400" />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>
            
            {/* Boutons en bas pour desktop */}
            <div className="lg:col-span-2 lg:flex lg:justify-end lg:space-x-2 mt-6 hidden">
              <Button
                variant={form.watch('status') === 'draft' ? 'outline' : 'default'}
                onClick={() => form.setValue('status', 'published')}
                disabled={saving}
                className={form.watch('status') === 'draft' ? 'bg-white/5 border-white/10 text-white hover:bg-white/10' : 'bg-yellow-500 hover:bg-yellow-600 text-black'}
              >
                <Eye className="mr-2 h-4 w-4" />
                Publier
              </Button>
              <Button
                variant={form.watch('status') === 'published' ? 'outline' : 'default'}
                onClick={() => form.setValue('status', 'draft')}
                disabled={saving}
                className={form.watch('status') === 'published' ? 'bg-white/5 border-white/10 text-white hover:bg-white/10' : 'bg-yellow-500 hover:bg-yellow-600 text-black'}
              >
                <EyeOff className="mr-2 h-4 w-4" />
                Brouillon
              </Button>
              <Button 
                type="submit" 
                form="blog-post-form" 
                disabled={saving}
                onClick={() => form.handleSubmit(onSubmit)()}
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Enregistrer
                  </>
                )}
              </Button>
            </div>
          </div>
        </Form>
      </div>
      {/* Sélecteur de média pour l'image à la une */}
      <MediaSelector
        isOpen={showMediaSelector}
        onClose={() => setShowMediaSelector(false)}
        onSelect={(url) => {
          setImagePreview(url);
          setShowMediaSelector(false);
        }}
        selectedUrl={imagePreview || ''}
        title="Sélectionner une image à la une"
        allowUpload={true}
      />
      
      {/* Sélecteur de média pour la galerie */}
      <MediaSelector
        isOpen={showGalleryMediaSelector}
        onClose={() => setShowGalleryMediaSelector(false)}
        onSelect={(urls) => {
          // Remplacer la galerie par les images sélectionnées
          if (Array.isArray(urls)) {
            setGalleryPreviews(urls);
          } else {
            setGalleryPreviews([urls]);
          }
          setShowGalleryMediaSelector(false);
        }}
        selectedUrls={galleryPreviews}
        title="Sélectionner des images pour la galerie"
        allowUpload={true}
        multiSelect={true}
      />
    </AdminLayout>
  );
};

export default BlogPostEdit;
